import { getCookie, setCookie } from '@/utils/cookie'

interface MatomoWindow extends Window {
  _paq?: Array<unknown[]>
  __csMatomoVisitorIdQueued__?: string
}

export interface MatomoVisitor {
  visitorId: string
  isNewVisitor: boolean
}

const BFUID_COOKIE = '_bfuid'
const MATOMO_COOKIE_DURATION = 30 * 60 * 1000
const MATOMO_VISITOR_ID_PATTERN = /^[0-9a-f]{16}$/i

let inMemoryVisitorId: string | undefined
let visitorCreatedDuringPageLoad: string | undefined

const getCookieOptions = () => ({
  expires: new Date(Date.now() + MATOMO_COOKIE_DURATION),
  sameSite: 'lax' as const,
  secure: window.location.protocol === 'https:',
})

const generateFallbackVisitorId = (): string => {
  const timestampHex = Date.now().toString(16).slice(-8).padStart(8, '0')
  const randomHex = Math.floor(Math.random() * 0x100000000)
    .toString(16)
    .padStart(8, '0')

  return `${timestampHex}${randomHex}`
}

const generateMatomoVisitorId = (): string => {
  try {
    const timestampHex = Date.now().toString(16).slice(-8).padStart(8, '0')
    const array = new Uint8Array(4)
    window.crypto.getRandomValues(array)
    const randomHex = Array.from(array)
      .map((value) => value.toString(16).padStart(2, '0'))
      .join('')

    return `${timestampHex}${randomHex}`
  } catch (error) {
    console.error(error)
    return generateFallbackVisitorId()
  }
}

const queueMatomoVisitor = (visitorId: string) => {
  const matomoWindow = window as MatomoWindow
  if (matomoWindow.__csMatomoVisitorIdQueued__ === visitorId) {
    return
  }

  matomoWindow._paq = matomoWindow._paq ?? []
  matomoWindow._paq.push(['setVisitorId', visitorId])
  matomoWindow._paq.push(['setCustomDimension', 33, visitorId])
  matomoWindow.__csMatomoVisitorIdQueued__ = visitorId
}

export const initializeMatomoVisitor = (): MatomoVisitor => {
  if (typeof window === 'undefined') {
    return { visitorId: '', isNewVisitor: false }
  }

  let cookieVisitorId: string | undefined
  try {
    cookieVisitorId = getCookie(BFUID_COOKIE)
  } catch (error) {
    console.error(error)
  }

  if (cookieVisitorId && !MATOMO_VISITOR_ID_PATTERN.test(cookieVisitorId)) {
    cookieVisitorId = undefined
  }

  const isNewVisitor = !cookieVisitorId && !inMemoryVisitorId
  const visitorId = cookieVisitorId
    ?? inMemoryVisitorId
    ?? generateMatomoVisitorId()

  inMemoryVisitorId = visitorId
  if (isNewVisitor) {
    visitorCreatedDuringPageLoad = visitorId
  }

  try {
    setCookie(BFUID_COOKIE, visitorId, getCookieOptions())
  } catch (error) {
    console.error(error)
  }

  queueMatomoVisitor(visitorId)

  return {
    visitorId,
    isNewVisitor: visitorCreatedDuringPageLoad === visitorId,
  }
}
